import {
  Alert,
  Autocomplete,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useContext, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import useSWR from 'swr';

import { UserContext } from '~/auth/userContext';
import { FieldAndCell } from '~/components/form/utils';
import { invoicesList, invoicesRetrieve } from '~/data/generated/endpoints/invoices/invoices';
import { parseApiResponse } from '~/data/parseApiResponse';
import {
  type InvoiceFormValues,
  invoiceSchema,
  InvoiceStatus,
  invoiceStatusToLabelMap,
} from '~/data/schemas/invoice';
import { formatCurrency } from '~/utils/format';

const creditableInvoiceSchema = invoiceSchema.pick({
  id: true,
  number: true,
  date: true,
  status: true,
  proposedAmount: true,
  reviewedAmount: true,
  savings: true,
});
export type CreditableInvoice = ReturnType<typeof creditableInvoiceSchema.parse>;

const CREDITABLE_LIMIT = 500;

const fetchCreditableInvoices = async ([, propertyId]: readonly [string, string]) => {
  const { data } = await invoicesList({
    property: propertyId,
    kind: ['invoice'],
    status: [InvoiceStatus.Approved, InvoiceStatus.PartialApproval],
    ordering: '-date',
    limit: CREDITABLE_LIMIT,
  });
  const rows = Array.isArray(data) ? data : data.results;
  return parseApiResponse(creditableInvoiceSchema.array(), rows, 'CreditableInvoice');
};

// The list above is capped, so a credit linked to an older invoice has nothing to
// render its label from. Fetch that one invoice so editing shows a real value
// rather than a blank field.
const fetchLinkedInvoice = async ([, invoiceId]: readonly [string, string]) => {
  const { data } = await invoicesRetrieve(invoiceId);
  return parseApiResponse(creditableInvoiceSchema, data, 'CreditableInvoice');
};

// Review already took this much off the invoice, so a credit for the same thing counts twice.
export const alreadyReducedBy = (invoice: CreditableInvoice | undefined): number => {
  if (!invoice) return 0;
  if (invoice.status === InvoiceStatus.PartialApproval) return invoice.savings ?? 0;
  return 0;
};

type InvoiceOption = { id: string; label: string };

const CreditFields: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { currencyCode } = useContext(UserContext);
  const { control, setValue } = useFormContext<InvoiceFormValues>();
  const propertyId = useWatch({ control, name: 'propertyId' });
  const creditedInvoiceId = useWatch({ control, name: 'creditedInvoiceId' });
  const creditTarget = useWatch({ control, name: 'creditTarget' });
  const keepOnFileOnly = useWatch({ control, name: 'keepOnFileOnly' });
  const target = creditTarget ?? (creditedInvoiceId ? 'invoice' : 'account');

  const { data: invoices } = useSWR(
    propertyId && target === 'invoice' ? (['creditableInvoices', propertyId] as const) : null,
    fetchCreditableInvoices,
  );

  const linkedIsMissing = Boolean(
    creditedInvoiceId && invoices && !invoices.some(({ id }) => id === creditedInvoiceId),
  );
  const { data: linkedInvoice } = useSWR(
    linkedIsMissing && target === 'invoice'
      ? (['creditableInvoice', creditedInvoiceId] as const)
      : null,
    fetchLinkedInvoice,
  );

  const creditable = useMemo(
    () => [...(invoices ?? []), ...(linkedInvoice ? [linkedInvoice] : [])],
    [invoices, linkedInvoice],
  );

  const options = useMemo<InvoiceOption[]>(
    () =>
      creditable.map((invoice) => ({
        id: invoice.id,
        label: [
          `#${invoice.number}`,
          invoice.date,
          formatCurrency({ value: invoice.proposedAmount ?? null, currencyCode }),
          invoiceStatusToLabelMap.get(invoice.status),
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [creditable, currencyCode],
  );

  const selected = creditable.find((invoice) => invoice.id === creditedInvoiceId);
  const reducedBy = target === 'invoice' ? alreadyReducedBy(selected) : 0;

  const chooseTarget = (_: React.MouseEvent<HTMLElement>, next: 'account' | 'invoice' | null) => {
    if (!next) return;
    setValue('creditTarget', next, { shouldDirty: true });
    if (next === 'account') {
      setValue('creditedInvoiceId', null, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <>
      <Typography variant="h6" my={2}>
        Credit
      </Typography>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary" id="credit-applies-to-label">
            Applies to
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={target}
            onChange={chooseTarget}
            disabled={disabled}
            aria-labelledby="credit-applies-to-label"
          >
            <ToggleButton value="account">Whole account</ToggleButton>
            <ToggleButton value="invoice">An invoice</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {target === 'invoice' ? (
          <Controller
            name="creditedInvoiceId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={options}
                value={options.find((option) => option.id === field.value) ?? null}
                onChange={(_, option) => field.onChange(option?.id ?? null)}
                onBlur={field.onBlur}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled || !propertyId}
                loading={Boolean(propertyId) && invoices === undefined}
                noOptionsText="No approved or partially approved invoices on this property"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Invoice"
                    required
                    error={Boolean(error)}
                    helperText={
                      error?.message ??
                      (propertyId
                        ? 'Only approved or partially approved invoices can receive a credit.'
                        : 'Choose the property first.') +
                        (invoices?.length === CREDITABLE_LIMIT
                          ? ` Only the ${CREDITABLE_LIMIT} most recent are listed.`
                          : '')
                    }
                  />
                )}
              />
            )}
          />
        ) : null}
        {reducedBy > 0 && selected && !keepOnFileOnly ? (
          <Alert severity="warning" data-testid="credit-already-reduced-warning">
            Invoice #{selected.number} was already reduced by{' '}
            {formatCurrency({ value: reducedBy, currencyCode })} in review. If this credit is for
            the same thing, keep it on file only so it isn&apos;t taken off spend twice.
          </Alert>
        ) : (
          <Alert severity="info" data-testid="credit-double-count-note">
            A credit lowers spend as soon as it&apos;s saved. Check the amount isn&apos;t already
            counted, for example taken off in review, and keep it on file only if it is.
          </Alert>
        )}
        <FieldAndCell
          field={{
            name: 'keepOnFileOnly',
            label: "Keep on file only (don't count toward spend)",
            type: 'checkbox',
            disabled,
            gridProps: { xs: 12 },
          }}
          control={control}
        />
      </Stack>
    </>
  );
};

export default CreditFields;
